/**
 * Visit, payment and order-line state machines.
 *
 * Three facts are tracked independently and must never be merged:
 *   ordered   — what the customer chose at the kiosk,
 *   performed — what the therapist actually delivered,
 *   paid      — what has been confirmed as paid.
 *
 * Payment `succeeded` can only be set by a payment provider or by staff with a PIN —
 * never by the kiosk.
 */
export const VISIT_STATES = [
    'booked',
    'checked_in',
    'in_service',
    'completed',
    'closed',
    'cancelled',
    'no_show',
];
const VISIT_TRANSITIONS = {
    booked: ['checked_in', 'cancelled', 'no_show'],
    checked_in: ['in_service', 'cancelled'],
    in_service: ['completed'],
    completed: ['closed'],
    closed: [],
    cancelled: [],
    no_show: [],
};
export const PAYMENT_STATES = [
    'none',
    'method_selected',
    'pending',
    'processing',
    'succeeded',
    'failed',
    'canceled',
    'expired',
    'refunded',
    'disputed',
];
const PAYMENT_TRANSITIONS = {
    none: ['method_selected'],
    method_selected: ['method_selected', 'pending', 'canceled'],
    pending: ['processing', 'succeeded', 'failed', 'canceled', 'expired'],
    processing: ['succeeded', 'failed'],
    succeeded: ['refunded', 'disputed'],
    failed: ['method_selected', 'pending'],
    canceled: [],
    expired: ['method_selected'],
    refunded: [],
    disputed: ['succeeded', 'refunded'],
};
export class TransitionError extends Error {
    machine;
    from;
    to;
    constructor(machine, from, to, reason) {
        super(`${machine}: ${from} → ${to} not allowed (${reason})`);
        this.machine = machine;
        this.from = from;
        this.to = to;
        this.name = 'TransitionError';
    }
}
export function canTransitionVisit(from, to) {
    return VISIT_TRANSITIONS[from].includes(to);
}
export function transitionVisit(from, to, actor) {
    if (!canTransitionVisit(from, to))
        throw new TransitionError('visit', from, to, 'invalid transition');
    if (actor === 'kiosk' && !(from === 'booked' && to === 'checked_in')) {
        throw new TransitionError('visit', from, to, 'kiosk may only check customers in');
    }
    return to;
}
export function canTransitionPayment(from, to) {
    return PAYMENT_TRANSITIONS[from].includes(to);
}
export function transitionPayment(from, to, actor) {
    if (!canTransitionPayment(from, to))
        throw new TransitionError('payment', from, to, 'invalid transition');
    const confirming = to === 'succeeded' || to === 'refunded';
    if (confirming && actor !== 'staff_pin' && actor !== 'payment_provider') {
        throw new TransitionError('payment', from, to, `${actor} cannot confirm money movement`);
    }
    if (actor === 'kiosk' && to !== 'method_selected' && to !== 'canceled') {
        throw new TransitionError('payment', from, to, 'kiosk may only select or cancel a method');
    }
    return to;
}
/** Discrepancies feed the therapist's closing screen and the end-of-day summary. */
export function lineDiscrepancies(lines) {
    const out = [];
    for (const l of lines) {
        if (l.performed > l.ordered)
            out.push({ serviceId: l.serviceId, kind: 'performed_not_ordered', quantity: l.performed - l.ordered });
        if (l.ordered > l.performed)
            out.push({ serviceId: l.serviceId, kind: 'ordered_not_performed', quantity: l.ordered - l.performed });
        if (l.performed > l.paid)
            out.push({ serviceId: l.serviceId, kind: 'performed_not_paid', quantity: l.performed - l.paid });
        if (l.paid > l.performed)
            out.push({ serviceId: l.serviceId, kind: 'paid_not_performed', quantity: l.paid - l.performed });
    }
    return out;
}
export function totalMinor(lines, fact) {
    return lines.reduce((sum, l) => sum + l.unitPriceMinor * l[fact], 0);
}
/**
 * True once the therapist has recorded what was actually done. From then on the bill follows
 * `performed` (services added on the tablet included, skipped services excluded); before that
 * it follows `ordered`.
 */
export function performanceRecorded(visit) {
    if (visit.state === 'completed' || visit.state === 'closed')
        return true;
    return visit.lines.some((l) => l.performed > 0);
}
/** Quantity of one line on the customer's bill (see `performanceRecorded`). */
export function billQuantity(line, visit) {
    return performanceRecorded(visit) ? line.performed : line.ordered;
}
/** Amount to pay for the visit, in minor units. The single source for every bill total shown. */
export function billTotalMinor(visit) {
    return totalMinor(visit.lines, performanceRecorded(visit) ? 'performed' : 'ordered');
}
//# sourceMappingURL=visit.js.map